// src/auth/sessionSyncMarker.js
//
// Tracks whether this browser has already completed a real sso-session-sync
// round trip (Login.jsx's syncAndContinue / Dashboard.jsx's lazy effect ->
// SsoSyncCallback.jsx) recently enough that Authentik's own session cookie
// is still known-good, so a redundant round trip can be skipped.
//
// CONFIRMED ROOT CAUSE (2026-09-03): every completed sync mints a NEW OAuth
// grant for this app's own client (SSO_PORTAL_CLIENT_ID) against the same
// Authentik user. When a second grant supersedes an earlier one for the
// same (user, client) pair, Authentik correctly fires a real Back-Channel
// Logout notification for the OLD grant -- but this app's receiver
// (backchannelLogout.js) records that as a blanket, username-scoped
// revocation with no session/grant-specific reasoning, which can then
// retroactively invalidate a brand-new bescom_token minted moments later
// for what looks like an unrelated login. Two real scenarios triggered
// this: (1) another connected app's own gateway redirect (e.g.
// md-dashboard) hitting sso.bescomapps.com/?next=... while already logged
// in, and (2) a second, consecutive fresh login in the same browser, whose
// own sync superseded the first login's still-"most recent" grant. Skipping
// a sync entirely when one already succeeded recently means no new grant
// gets minted, so no supersession/notification happens either -- the
// browser's already-live Authentik cookie is sufficient on its own for a
// same-origin app-card / ?next= redirect; the round trip through
// sso-session-sync is Authentik's login-stage bridge, not a requirement for
// every single navigation.
//
// localStorage (not sessionStorage): needs to persist across the full-page
// navigations this whole flow is built from, and be visible across tabs of
// the same origin -- same reasons bescom_pending_next/bescom_needs_profile
// already use localStorage.

const MARKER_KEY = 'bescom_authentik_synced_at';
// Comfortably under the sso-session-sync flow's own confirmed 1-hour
// session_duration (its User Login Stage) -- see header above. Not tied
// exactly to it: a wide-but-safe margin so a slow tab, clock drift, etc.
// can't strand someone with a marker that outlives the session it promises.
const SYNC_TTL_MS = 45 * 60 * 1000;

export function markSynced() {
  try {
    localStorage.setItem(MARKER_KEY, Date.now().toString());
  } catch (e) {
    // Best-effort -- private browsing / storage disabled just means the
    // check below always misses, i.e. falls back to the old always-sync
    // behavior. Never a hard failure.
  }
}

export function wasRecentlySynced() {
  try {
    const at = Number(localStorage.getItem(MARKER_KEY));
    return Number.isFinite(at) && at > 0 && (Date.now() - at) < SYNC_TTL_MS;
  } catch (e) {
    return false;
  }
}

export function clearSyncMarker() {
  try {
    localStorage.removeItem(MARKER_KEY);
  } catch (e) {
    // Best-effort.
  }
}

// CONFIRMED BUG (2026-09-08): wasRecentlySynced() above answers "did a
// real sync succeed recently", which is exactly right for the normal case
// but silently wrong when Authentik's own session gets killed out from
// under us moments later by the upstream batch session-end bug (see the
// authentik-upstream-batch-session-end-bug memory) — confirmed via a real
// incident where an outpost-fronted app (BICC Dashboard) looped forever
// between here and Authentik's own outpost-login-redirect flow, because
// Login.jsx kept trusting the same now-stale marker on every bounce-back
// and kept sending the browser straight to `next` with no real
// verification. This tracks, per `next` target, whether we've ALREADY
// tried that "trust the marker" fast path recently and landed back on
// this same gateway again — if so, the marker was wrong, so the caller
// should force a real sync instead of repeating the same mistake, with a
// hard cap (see MAX_NEXT_ATTEMPTS) so a genuinely broken Authentik-side
// state shows a manual retry button instead of looping silently forever.
//
// sessionStorage (not localStorage): scoped to this tab's session, and
// self-expires via the timestamp check below rather than needing an
// explicit clear on the success path (though SsoSyncCallback.jsx does
// clear it anyway once a real sync actually lands, for a clean slate).
const NEXT_ATTEMPT_KEY = 'bescom_gateway_next_attempts';
const NEXT_ATTEMPT_WINDOW_MS = 20000; // covers a full outpost-login-redirect round trip
export const MAX_NEXT_ATTEMPTS = 3;

function readNextAttempts(nextUrl) {
  try {
    const raw = sessionStorage.getItem(NEXT_ATTEMPT_KEY);
    if (!raw) return 0;
    const { url, at, count } = JSON.parse(raw);
    if (url !== nextUrl || (Date.now() - at) >= NEXT_ATTEMPT_WINDOW_MS) return 0;
    return count;
  } catch (e) {
    return 0;
  }
}

// Call once per attempt at reaching `nextUrl` (whether via the fast path
// or a forced real sync) — returns the attempt number so far (1 = first
// attempt this window).
export function recordNextAttempt(nextUrl) {
  const count = readNextAttempts(nextUrl) + 1;
  try {
    sessionStorage.setItem(NEXT_ATTEMPT_KEY, JSON.stringify({ url: nextUrl, at: Date.now(), count }));
  } catch (e) {
    // Best-effort — private browsing / storage disabled just means every
    // attempt looks like a fresh first attempt, i.e. falls back to the
    // old always-trust-the-marker behavior. Never a hard failure.
  }
  return count;
}

export function clearGatewayAttempts() {
  try {
    sessionStorage.removeItem(NEXT_ATTEMPT_KEY);
  } catch (e) {
    // Best-effort.
  }
}
